import { useMemo } from "react";

import { Avatar, AvatarFallback, AvatarImage } from "shared/ui/shadcn/avatar";
import { getFullEntityName, getInitialsFromUsername } from "shared/lib/helpers";
import { Skeleton } from "shared/ui/shadcn/skeleton";
import { UserIsOnlineMapI } from "shared/lib/interfaces/chat";

import { ChatUserOnlineIndicator } from "../shared/online-indicator";
import type { ChatContactI } from "../../types";
import { getChatContactOnlineStatus } from "../../utils";

interface ChatHeaderPropsI {
  onlineStatusMap: UserIsOnlineMapI | undefined;
  contact: ChatContactI | undefined;
  role: string | undefined;
}

export const ChatHeader = ({
  contact,
  role,
  onlineStatusMap,
}: ChatHeaderPropsI) => {
  const { contactFullName, contactInitials, avatarURL, isOnline } =
    useMemo(() => {
      if (!contact) {
        return {
          contactFullName: null,
          contactInitials: null,
          avatarURL: "",
          isOnline: null,
        };
      }

      const contactFullName = getFullEntityName(contact);

      return {
        contactFullName,
        contactInitials: getInitialsFromUsername(contactFullName),
        avatarURL: contact?.profile_pic_url,
        isOnline: getChatContactOnlineStatus(onlineStatusMap, contact),
      };
    }, [contact, onlineStatusMap]);

  return (
    <div className="flex items-center gap-4 border-b p-4">
      <div className="relative">
        {avatarURL === null ? (
          <Skeleton className="h-12 w-12 rounded-full" />
        ) : (
          <Avatar className="h-12 w-12">
            <AvatarImage src={avatarURL} alt={contactFullName || ""} />
            <AvatarFallback>{contactInitials}</AvatarFallback>
          </Avatar>
        )}

        {isOnline !== null && (
          <ChatUserOnlineIndicator
            className="animate-fadein"
            isOnline={isOnline}
          />
        )}
      </div>

      <div>
        {contactFullName === null ? (
          <Skeleton className="mb-2 h-5 w-[120px] rounded-md" />
        ) : (
          <p className="typography-body-3-semibold mb-1">{contactFullName}</p>
        )}

        {role ? (
          <p className="typography-body-4 mt-1 text-gray-500">{role}</p>
        ) : (
          <Skeleton className="mt-1 h-3 w-[80px]" />
        )}

        {isOnline === null ? (
          <Skeleton className="mt-1 h-3 w-[50px] rounded-md" />
        ) : (
          <p className="animate-fadein typography-body-5 mt-1 text-gray-400">
            {isOnline ? "Online" : "Offline"}
          </p>
        )}
      </div>
    </div>
  );
};
